/**
 * editCustomerContext (EDIT-SUITE-01, 34i97) — ONE suite, many doors.
 *
 * The 360 keeps its pencils (People ✎, Watch For Edit, the tech badge, Lead
 * source Correct, the ··· menu, the E key) but every one of them opens the SAME
 * Edit Customer panel at its section. The provider lives on the 360 page; any
 * card calls `useEditCustomer().open("people")`. Outside the 360 (no provider)
 * the hook is a no-op, so shared blocks keep working wherever they render.
 */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

export type EditSection = "account" | "people" | "reach" | "properties" | "watch" | "equipment" | "plan" | "notes" | "links";

export const EDIT_SECTIONS: { key: EditSection; label: string }[] = [
  { key: "account", label: "Name & Account" },
  { key: "people", label: "People & Lines" },
  { key: "reach", label: "Reach" },
  { key: "properties", label: "Properties" },
  { key: "watch", label: "Watch For" },
  { key: "equipment", label: "Equipment" },
  { key: "plan", label: "Plan" },
  { key: "notes", label: "Notes & Snapshot" },
  { key: "links", label: "Links & History" },
];

interface EditCustomerCtx {
  /** The section the panel is open at, or null when closed. */
  section: EditSection | null;
  open: (section?: EditSection) => void;
  close: () => void;
  /** True when a provider is mounted (the 360 page) — doors fall back to their old editors otherwise. */
  available: boolean;
}

const Ctx = createContext<EditCustomerCtx>({ section: null, open: () => {}, close: () => {}, available: false });

export function EditCustomerProvider({ children }: { children: ReactNode }) {
  const [section, setSection] = useState<EditSection | null>(null);
  const open = useCallback((s: EditSection = "account") => setSection(s), []);
  const close = useCallback(() => setSection(null), []);
  const value = useMemo<EditCustomerCtx>(() => ({ section, open, close, available: true }), [section, open, close]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useEditCustomer(): EditCustomerCtx {
  return useContext(Ctx);
}
